import React, { FC, useContext, useEffect, useMemo } from 'react';
import { Row, Col } from 'antd';
import { Formik, Form } from 'formik';
import COButton from 'shared/components/COButton';
import COSelect from 'shared/components/COSelect';
import InputField from 'shared/components/InputField';
import OrganizationModal from 'shared/components/OrganizationModal';
import styles from './createTargetModal.module.scss';
import { ButtonSize } from 'enums/ButtonEnums';
import { customTargetFields, scienceBasedFields } from './createTargetFields';
import TargetService from 'services/TargetService/target.service';
import { ParentOrganization, Target } from 'models/Target/Target.model';
import { useParentOrganizationContext } from 'context/ParentOrganisationContext';
import { useState } from 'react';
import TargetValidationSchema from './validationSchema';
import { SbtiTarget } from 'models/Sbti/sbti.model';
import { TargetTexts } from '../constants';
import CancelFormModal from 'shared/components/CancelFormModal';
import { CompanySetupEnum } from 'enums/CompanySetupEnums';
import { HelpDeskTooltipContext } from 'context/HelpDeskTooltipContext';
import { HelpDeskInputFieldEnum } from 'context/HelpDeskTooltipContext/helpDeskInputFieldEnum';
import { handleSubmitUtil } from './targetUtils';

interface CreateTargetModalProps {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  isCustomTarget?: boolean;
  selectedTargetId?: number;
  sbtiTargets?: SbtiTarget[];
  fetchSbtiTargets?: () => Promise<void>;
  isEdit?: boolean;
  setIsEdit?: React.Dispatch<React.SetStateAction<boolean>> | undefined;
  organization?: ParentOrganization;
  setSelectedTarget?: React.Dispatch<React.SetStateAction<string>>;
  setSelectedTargetId?: React.Dispatch<
    React.SetStateAction<number | undefined>
  >;
  setSelectedIndex?: React.Dispatch<React.SetStateAction<number>>;
}

const CreateTargetModal: FC<CreateTargetModalProps> = props => {
  const {
    visible,
    setVisible,
    isCustomTarget,
    selectedTargetId,
    sbtiTargets,
    fetchSbtiTargets,
    isEdit,
    setIsEdit,
    organization,
    setSelectedTarget,
    setSelectedTargetId,
    setSelectedIndex,
  } = props;
  const {
    loading,
    createTarget,
    fetchTargetCoverageMeta,
    targetCoverageMeta,
    resetTargetCoverageMeta,
    fetchTargeYearsMeta,
    targetYearsMeta,
    updateTarget,
    resetTargetYearsMeta,
  } = TargetService();
  const { organizationData } = useParentOrganizationContext();
  const { openTooltipModal } = useContext(HelpDeskTooltipContext);
  const [initialFilteredValues, setInitialFilteredValues] = useState<Target>(
    new Target()
  );
  const [isCancelModal, setIsCancelModal] = useState(false);
  const closeHandler = () => {
    setVisible(false);
    setIsCancelModal(false);
    resetTargetCoverageMeta();
    setIsEdit?.(false);
  };
  useEffect(() => {
    const filteredTarget = sbtiTargets?.filter(
      target => target?.id === selectedTargetId
    );
    if (filteredTarget?.length) {
      if (!isCustomTarget) {
        const { totalReduction, ...restFilteredValues } =
          filteredTarget[0] as Target;
        setInitialFilteredValues(restFilteredValues);
      } else {
        setInitialFilteredValues(filteredTarget[0]);
      }
    } else {
      setInitialFilteredValues(new Target());
    }
    // To prefill the dropdown options in edit mode for science based target
    if (isEdit && !isCustomTarget) {
      const { isSME, targetType, baselineYear } = filteredTarget?.[0] as Target;
      fetchTargetCoverageMeta(isSME);
      fetchTargeYearsMeta(isSME, targetType, baselineYear);
    }
  }, [selectedTargetId, sbtiTargets, isCustomTarget, isEdit]);

  const handleAlertModalOpen = () => setIsCancelModal(true);

  const toggleDeleteModalVisibility = () => setIsCancelModal(prev => !prev);

  const desiredInitialValues = isEdit ? initialFilteredValues : new Target();

  const initialValues = {
    ...desiredInitialValues,
    organizationId: isEdit
      ? initialFilteredValues?.organizationId
      : organization?.id,
    baselineYear: organizationData?.company?.baselineYear,
    targetCategory: isCustomTarget
      ? TargetTexts.CUSTOM
      : TargetTexts.SCIENCE_BASED,
  };

  const handleSubmit = async (values: Target) => {
    await handleSubmitUtil({
      values,
      isEdit,
      initialFilteredValues,
      updateTarget,
      createTarget,
      closeHandler,
      setSelectedTarget,
      setSelectedTargetId,
      setSelectedIndex,
      sbtiTargets,
      fetchSbtiTargets,
    });
  };

  const desiredTargetFields = useMemo(() => {
    const parentOrgaizationDetails = isEdit
      ? {
          name: initialFilteredValues?.organizationName,
          id: initialFilteredValues?.organizationId,
        }
      : { name: organization?.name, id: organization?.id };
    return isCustomTarget
      ? customTargetFields(
          organizationData,
          targetCoverageMeta,
          targetYearsMeta,
          isCustomTarget,
          parentOrgaizationDetails as ParentOrganization
        )
      : scienceBasedFields(
          organizationData,
          targetCoverageMeta,
          targetYearsMeta,
          isCustomTarget,
          parentOrgaizationDetails as ParentOrganization
        );
  }, [
    isCustomTarget,
    organizationData,
    targetCoverageMeta,
    targetYearsMeta,
    initialFilteredValues,
    isEdit,
    organization,
  ]);
  const handleToolTipOpen = (fieldName?: HelpDeskInputFieldEnum) => () =>
    fieldName && openTooltipModal(fieldName);

  const onChangeHanlder =
    (values: Target, setFieldValue: Function, field?: string) =>
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!isCustomTarget) {
        switch (field) {
          case TargetTexts.IS_SME:
            setFieldValue(TargetTexts.TARGET_COVERAGE, null);
            setFieldValue(TargetTexts.TARGET_YEAR, null);
            if (values?.targetType) {
              fetchTargetCoverageMeta(Boolean(event));
              fetchTargeYearsMeta(
                Boolean(event),
                values?.targetType,
                values.baselineYear
              );
            } else {
              resetTargetCoverageMeta();
              resetTargetYearsMeta();
            }
            break;
          case TargetTexts.TARGET_TYPE:
            if (event && values?.isSME !== undefined) {
              fetchTargetCoverageMeta(values?.isSME);
              fetchTargeYearsMeta(
                values?.isSME,
                event?.toString(),
                values.baselineYear
              );
            } else {
              resetTargetCoverageMeta();
              resetTargetYearsMeta();
            }
            setFieldValue(TargetTexts.TARGET_COVERAGE, null);
            setFieldValue(TargetTexts.TARGET_YEAR, null);
            break;
        }
      }
    };
  if (!visible) return null;
  return (
    <>
      <OrganizationModal
        visible={visible}
        width={680}
        onClose={handleAlertModalOpen}
        title={
          isEdit
            ? TargetTexts.EDIT_TARGET
            : isCustomTarget
            ? TargetTexts.CREATE_CUSTOM_TEXT
            : TargetTexts.CREATE_TEXT
        }
        children={
          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={TargetValidationSchema}
            enableReinitialize>
            {({ values, setFieldValue }) => (
              <Form>
                <div className={styles.createTargetContainer}>
                  <div className={styles.fieldContainer}>
                    <Row gutter={[20, 30]}>
                      {desiredTargetFields?.map(field =>
                        field?.isSelect ? (
                          <Col span={12} key={field.name}>
                            <COSelect
                              name={field.name}
                              placeholderstring={field.placeholder}
                              label={field.placeholder}
                              options={field.options}
                              listHeight={200}
                              allowClear={false}
                              disabled={field.disabled}
                              isShowLabel
                              onChange={onChangeHanlder(
                                values,
                                setFieldValue,
                                field.name
                              )}
                              info
                              infoClick={handleToolTipOpen(field.helpdesk)}
                            />
                          </Col>
                        ) : (
                          <Col
                            span={field?.isTextarea ? 24 : 12}
                            key={field.name}>
                            <InputField
                              textarea={field.isTextarea}
                              name={field.name}
                              placeholderstring={field.placeholder}
                              label={field.label}
                              onKeyDown={field.onKeyDown}
                              isShowLabel
                              info
                              infoClick={handleToolTipOpen(field.helpdesk)}
                              className={field?.className}
                            />
                          </Col>
                        )
                      )}
                    </Row>
                  </div>
                </div>
                <div className={styles.createTargetButtonContainer}>
                  <div className={styles.buttonContainer}>
                    <COButton
                      primary
                      submit
                      size={ButtonSize.SMALL}
                      loading={loading}>
                      {isEdit ? TargetTexts.SUBMIT : TargetTexts.CREATE}
                    </COButton>
                  </div>
                  <div className={styles.buttonContainer}>
                    <COButton
                      secondary
                      size={ButtonSize.SMALL}
                      onClick={handleAlertModalOpen}>
                      Cancel
                    </COButton>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        }
      />
      <CancelFormModal
        handleModalCancel={toggleDeleteModalVisibility}
        handleModalConfirm={closeHandler}
        showModalState={isCancelModal}
        heading={CompanySetupEnum.CloseFormHeading}
        subHeading={
          isEdit
            ? TargetTexts.ALERT_SUB_HEADING_EDIT
            : TargetTexts.ALERT_SUB_HEADING
        }
      />
    </>
  );
};

export default CreateTargetModal;
